import React from 'react';
import { eventType, OPERATION_TYPE, operationType, PATHNAME } from '../../../../util/varibles/constants';
import { durationTxt, formatNumber } from '../../../../util/varibles/global';
import { TOperation2 } from '../../../../util/varibles/interface';
import stylesContainer from '../../style.module.scss';
import StartPoint from './StartPoint';

interface IProps {
    operation: TOperation2
    vesselId: string
}

type TInvoice = NonNullable<TOperation2['invoices']>[number];

const getInvoiceTypeName = (invoice: Pick<TInvoice, 'operation_type' | 'event_type'>) => {
    if (invoice.operation_type === OPERATION_TYPE.EVENT) {
        return eventType[invoice.event_type!]?.name || operationType[invoice.operation_type]?.name;
    }

    return operationType[invoice.operation_type]?.name;
};

const OperationCost: React.FC<IProps> = ({ operation, vesselId }) => {
    const billings = operation.billings || [];
    const invoices = operation.invoices || [];
    const cleaningAgent = operation.cleaning_agent ?? 0;

    const invoiceByOperationId = new Map(invoices.map(invoice => [invoice.operation_id, invoice]));
    const totalDuration = billings.reduce((total, billing) => total + billing.duration, 0);
    const totalCost = billings.reduce((total, billing) => total + billing.cost, 0) + cleaningAgent;

    const billingGroups = Array.from(billings.reduce((groups, billing) => {
        const key = billing.operation_id;
        const group = groups.get(key) || [];
        group.push(billing);
        groups.set(key, group);
        return groups;
    }, new Map<string | undefined, typeof billings>()).entries()).map(([operationId, items]) => ({
        operationId,
        items,
        invoice: operationId ? invoiceByOperationId.get(operationId) : undefined,
        showTotal: items.length > 1,
        duration: items.reduce((total, item) => total + item.duration, 0),
        cost: items.reduce((total, item) => total + item.cost, 0),
    }));
    const { hourly_cost } = operation;
    const siteName = 'site_name' in operation ? operation.site_name : undefined;
    return <div className={`${stylesContainer.section} col-span-2 mb-[15px] w-full`}>
        <div className={`${stylesContainer['header-box']} `}>
            <div>Operation cost</div>
            <StartPoint operation={operation} vesselId={vesselId} />
        </div>
        {billings.length ? <div className='w-full overflow-x-auto border-t border-[var(--border2)]'>
            <table className='w-full min-w-max border-collapse text-left text-[15px] text-[var(--colorMain)]'>
                <thead>
                    <tr>
                        <th rowSpan={2} className='text-comment min-w-[110px] border-b border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Hourly rate</th>
                        {billingGroups.map((group, index) => {
                            const { invoice } = group;
                            const operationLabel = invoice
                                ? `#${invoice.operation_code} | ${getInvoiceTypeName(invoice)}`
                                : '';

                            return <th
                                key={group.operationId || index}
                                colSpan={group.items.length + (group.showTotal ? 1 : 0)}
                                className={`border-r border-[var(--border2)] px-[8px] pt-[5px] font-normal ${group.items.length > 1 ? 'text-center' : 'text-left'}`}
                            >
                                {invoice && (invoice.operation_id === operation.id
                                    ? <span title={operationLabel}>{operationLabel}</span>
                                    : <a
                                        className='link !text-[15px]'
                                        href={`${PATHNAME.OPERATION}/${invoice.tenant_id}/${invoice.operation_id}`}
                                        target='_blank'
                                        rel='noreferrer'
                                        title={operationLabel}
                                    >
                                        {operationLabel}
                                    </a>)}
                            </th>;
                        })}
                        <th rowSpan={2} className='text-comment min-w-[110px] border-b border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Σ Duration</th>
                        <th rowSpan={2} className='text-comment min-w-[110px] border-b border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Cleaning agent</th>
                        <th rowSpan={2} className='text-comment min-w-[110px] border-b border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Σ Invoice cost</th>
                    </tr>
                    <tr>
                        {billingGroups.map((group, groupIndex) => <React.Fragment key={group.operationId || groupIndex}>
                            {group.items.map((billing, index) => <th
                                key={index}
                                className={`capitalize text-[var(--colorSub)] min-w-[110px] border-b border-[var(--border2)] px-[8px] pb-[5px] text-[13px] font-semibold ${!group.showTotal && index === group.items.length - 1 ? 'border-r' : ''}`}
                            >
                                <div className='flex flex-col justify-end'>
                                    <span>
                                        {billing.name}
                                        {billing.actual_duration != null && <span className='text-[#ABA89C] text-[13px] font-semibold normal-case'> (Actual)</span>}
                                    </span>
                                </div>
                            </th>)}
                            {group.showTotal && <th className='text-comment min-w-[110px] border-b border-r border-[var(--border2)] px-[8px] pb-[5px] text-[13px] font-semibold'>Standard Total</th>}
                        </React.Fragment>)}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td rowSpan={2} className='border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom'>{formatNumber(hourly_cost)}</td>
                        {billingGroups.map((group, groupIndex) => <React.Fragment key={group.operationId || groupIndex}>
                            {group.items.map((billing, index) => <td
                                key={index}
                                className={`border-b border-[var(--border2)] px-[8px] py-[5px] ${!group.showTotal && index === group.items.length - 1 ? 'border-r' : ''}`}
                            >
                                {durationTxt(billing.duration)}
                                {billing.actual_duration != null && <span className='text-[#ABA89C] text-[15px] font-normal'> ({durationTxt(billing.actual_duration, '0h').trim()})</span>}
                            </td>)}
                            {group.showTotal && <td className='border-b border-r border-[var(--border2)] px-[8px] py-[5px]'>{durationTxt(group.duration)}</td>}
                        </React.Fragment>)}
                        <td rowSpan={2} className='border-r border-[var(--border2)] px-[8px] py-[5px] align-top'>{durationTxt(totalDuration)}</td>
                        <td rowSpan={2} className='border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom'>{formatNumber(cleaningAgent)}</td>
                        <td rowSpan={2} className='px-[8px] py-[5px] align-bottom font-semibold'>{formatNumber(totalCost)}</td>
                    </tr>
                    <tr>
                        {billingGroups.map((group, index) => <React.Fragment key={group.operationId || index}>
                            {group.showTotal && <td colSpan={group.items.length} />}
                            <td className='border-r border-[var(--border2)] px-[8px] py-[5px] text-left'>
                                {formatNumber(group.cost)}
                            </td>
                        </React.Fragment>)}
                    </tr>
                </tbody>
            </table>
        </div> : <div className='w-full overflow-x-auto border-t border-[var(--border2)]'>
            <table className='w-full min-w-max border-collapse text-left text-[15px] text-[var(--colorMain)]'>
                <thead>
                    <tr>
                        <th className='text-comment w-[110px] min-w-[110px] border-b border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Hourly rate</th>
                        <th className='min-w-[140px] border-b border-r border-[var(--border2)] px-[8px] py-[5px] font-normal'>
                            <div>{`#${operation.operation_code} | ${getInvoiceTypeName(operation)}`}</div>
                            <div className='text-comment text-[13px] font-semibold'>{siteName}</div>
                        </th>
                        <th className='text-comment w-[110px] min-w-[110px] border-b border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Σ Duration</th>
                        <th className='text-comment w-[110px] min-w-[110px] border-b border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Cleaning agent</th>
                        <th className='text-comment w-[110px] min-w-[110px] border-b border-[var(--border2)] px-[8px] py-[5px] align-bottom text-[13px] font-semibold'>Σ Invoice cost</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className='h-[40px]'>
                        <td rowSpan={2} className='border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom'>{formatNumber(hourly_cost)}</td>
                        <td className='border-b border-r border-[var(--border2)]' />
                        <td rowSpan={2} className='border-r border-[var(--border2)]' />
                        <td rowSpan={2} className='border-r border-[var(--border2)] px-[8px] py-[5px] align-bottom'>{formatNumber(cleaningAgent)}</td>
                        <td rowSpan={2} className='px-[8px] py-[5px] align-bottom font-semibold'>{formatNumber(totalCost)}</td>
                    </tr>
                    <tr className='h-[40px]'>
                        <td className='border-r border-[var(--border2)]' />
                    </tr>
                </tbody>
            </table>
        </div>}
    </div>;
};

export default OperationCost;
